/**
 * EventProfilePage — V2 orchestrator for the Event profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Single polling cycle (600s)
 *   - Tab state (details, logs, notes)
 *   - Dialog state for note add/edit, reason edit, delete confirmation
 *   - Toast notifications
 *
 * Props:
 *   id — Event ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './event-profile-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoSection from './components/BasicInfoSection.jsx';
import EventFlowCard from './components/EventFlowCard.jsx';
import LifecycleLogsTab from './components/LifecycleLogsTab.jsx';
import CallLogsTab from './components/CallLogsTab.jsx';
import NotesTab from './components/NotesTab.jsx';
import EventNoteDialog from '../../reports/alert-details/components/EventNoteDialog.jsx';
import EditReasonDialog from '../../reports/alert-details/components/EditReasonDialog.jsx';

import {
    getSession, getCampusId, getUserId, getSessionId,
    getCampusList, getRoleWeight, getCampusConfig,
} from '../../../services/session.js';
import {
    fetchEvent, fetchEventNotes, fetchPreformNotes,
    fetchEventLogs, fetchEventCallLogs, fetchReasonCodes, fetchReasonEdits,
    addEventNote, editEventNote, deleteEventNote, editEventReason,
} from './api/eventProfileApi.js';
import { iAmHigherAuthority as iAmHigherAuth } from '../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const EventProfilePage = ({ id }) => {

    // --------------------------------------------------------------------------
    // SESSION
    // --------------------------------------------------------------------------
    const campusId = useMemo(() => getCampusId(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const campusName = useMemo(() => {
        const s = getSession();
        return s?.campusData?.[campusId.toString()]?.name || s?.campus || '';
    }, [campusId]);
    const myRoleWeight = useMemo(() => getRoleWeight(), []);
    const editRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);

    const eventId = id || '';

    // Campus config for the active campus — the one canonical accessor for all
    // campus-specific settings (feature flags, timezone, cloud/grandstream) used here.
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const eventNotesActive = campusConfig?.event_notes_active || false;
    const eventNotesPreform = campusConfig?.event_notes_preform || false;
    const eventNotesFreeform = campusConfig?.event_notes_freeform || false;
    const isReasonEditable = campusConfig?.is_reason_editable || false;
    const isCloud = !!campusConfig?.is_cloud;
    const isGrandstream = !!campusConfig?.is_grandstream;
    const tzOffset = parseInt(campusConfig?.tz_offset || 0);

    // --------------------------------------------------------------------------
    // TOAST
    // --------------------------------------------------------------------------
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // --------------------------------------------------------------------------
    // TABS
    // --------------------------------------------------------------------------
    const [activeTab, setActiveTab] = useState('details');

    // --------------------------------------------------------------------------
    // CORE STATE
    // --------------------------------------------------------------------------
    const [loading, setLoading] = useState(true);
    const [eventData, setEventData] = useState(null);
    const [noteData, setNoteData] = useState([]);
    const [prenoteData, setPrenoteData] = useState([]);
    const [reasonCodes, setReasonCodes] = useState([]);
    const [reasonEdits, setReasonEdits] = useState([]);
    const [lifecycleLogs, setLifecycleLogs] = useState([]);
    const [callLogs, setCallLogs] = useState([]);

    // --------------------------------------------------------------------------
    // DIALOG STATE
    // --------------------------------------------------------------------------
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [editNoteData, setEditNoteData] = useState(null); // null = add mode, object = edit mode
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteNoteIds, setDeleteNoteIds] = useState([]);
    const [saving, setSaving] = useState(false);

    // --------------------------------------------------------------------------
    // DATA FETCHING (600s polling cycle)
    // --------------------------------------------------------------------------
    const fetchAllData = useCallback(async () => {
        const fetches = [
            fetchEvent(eventId, campusId),
            fetchEventNotes(eventId, campusId),
            eventNotesPreform ? fetchPreformNotes(campusId) : Promise.resolve([]),
            isCloud ? fetchEventLogs(eventId) : Promise.resolve([]),
            isReasonEditable ? fetchReasonCodes(campusId) : Promise.resolve([]),
            isReasonEditable ? fetchReasonEdits(eventId, campusId) : Promise.resolve([]),
            // Grandstream call logs — skipped (resolves []) on non-grandstream
            // campuses so the proc isn't called pointlessly.
            isGrandstream ? fetchEventCallLogs(eventId, campusId) : Promise.resolve([]),
        ];
        const [
            eventResult, notesResult, prenotes,
            logsResult, reasonCodesResult, reasonEditsResult,
            callLogsResult,
        ] = await Promise.all(fetches);

        if (eventResult?.length > 0) {
            setEventData(eventResult[0]);
        }
        setNoteData(notesResult || []);
        setPrenoteData(prenotes || []);
        setLifecycleLogs(logsResult || []);
        setReasonCodes(reasonCodesResult || []);
        setReasonEdits(reasonEditsResult || []);
        setCallLogs(callLogsResult || []);
        setLoading(false);
    }, [eventId, campusId, eventNotesPreform, isCloud, isReasonEditable, isGrandstream]);

    useEffect(() => {
        fetchAllData();
        const interval = setInterval(fetchAllData, 600_000);
        return () => clearInterval(interval);
    }, [fetchAllData]);

    // --------------------------------------------------------------------------
    // ACTION HANDLERS
    // --------------------------------------------------------------------------

    /** Add a new note */
    const handleAddNote = useCallback(async (evtId, noteText) => {
        setSaving(true);
        const result = await addEventNote(evtId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);

        if (result && result[0] === true) {
            showToast('success', 'Note added successfully.');
            setNoteDialogOpen(false);
            fetchAllData();
        } else {
            showToast('error', `Error adding note${result?.[1] ? ': ' + result[1] : ''}.`);
        }
    }, [campusId, showToast, fetchAllData]);

    /** Edit an existing note */
    const handleEditNote = useCallback(async (evtId, noteText, noteId) => {
        setSaving(true);
        const result = await editEventNote(noteId, evtId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);

        if (result && result[0] === true) {
            showToast('success', 'Note updated successfully.');
            setNoteDialogOpen(false);
            setEditNoteData(null);
            fetchAllData();
        } else {
            showToast('error', `Error updating note${result?.[1] ? ': ' + result[1] : ''}.`);
        }
    }, [campusId, showToast, fetchAllData]);

    /** Save note — routes to add or edit based on noteId presence */
    const handleSaveNote = useCallback((evtId, noteText, noteId) => {
        if (noteId) {
            handleEditNote(evtId, noteText, noteId);
        } else {
            handleAddNote(evtId, noteText);
        }
    }, [handleAddNote, handleEditNote]);

    /** Open delete confirmation dialog */
    const handleDeleteNotes = useCallback((noteIds) => {
        setDeleteNoteIds(noteIds);
        setDeleteDialogOpen(true);
    }, []);

    /** Confirm note deletion */
    const confirmDeleteNotes = useCallback(async () => {
        setSaving(true);
        let allSuccess = true;
        for (const noteId of deleteNoteIds) {
            const result = await deleteEventNote(noteId, getUserId(), campusId);
            if (!result || result[0] !== true) {
                allSuccess = false;
            }
        }
        setSaving(false);
        setDeleteDialogOpen(false);
        setDeleteNoteIds([]);

        if (allSuccess) {
            showToast('success', `${deleteNoteIds.length} note${deleteNoteIds.length !== 1 ? 's' : ''} deleted.`);
        } else {
            showToast('error', 'Error deleting one or more notes.');
        }
        fetchAllData();
    }, [deleteNoteIds, campusId, showToast, fetchAllData]);

    /** Save edited reason */
    const handleSaveReason = useCallback(async (evtId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(evtId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);

        if (result && result[0] === true) {
            showToast('success', 'Event reason updated.');
            setReasonDialogOpen(false);
            fetchAllData();
        } else {
            showToast('error', `Error updating reason${result?.[1] ? ': ' + result[1] : ''}.`);
        }
    }, [campusId, showToast, fetchAllData]);

    /** Open add note dialog */
    const openAddNote = useCallback(() => {
        setEditNoteData(null);
        setNoteDialogOpen(true);
    }, []);

    /** Open edit note dialog */
    const openEditNote = useCallback((note) => {
        setEditNoteData(note);
        setNoteDialogOpen(true);
    }, []);

    /** Open reason edit dialog */
    const openReasonDialog = useCallback(() => {
        setReasonDialogOpen(true);
    }, []);

    // --------------------------------------------------------------------------
    // DERIVED VALUES
    // --------------------------------------------------------------------------
    const displayId = useMemo(() => {
        if (!eventId) return '';
        const parts = String(eventId).split('@');
        return parts[1] || eventId;
    }, [eventId]);

    const bannerName = useMemo(() => {
        if (!eventData) return '';
        const cat = eventData.event_category_name || '';
        const type = eventData.event_type_name || '';
        return cat && type ? `${cat} / ${type}` : cat || type || 'Event';
    }, [eventData]);

    const bannerMeta = useMemo(() => [
        { label: 'Resident', value: eventData?.resident_name || '\u2014' },
        { label: 'Area', value: eventData?.area_name || '\u2014' },
        { label: 'Response Time', value: eventData?.response_time != null ? `${eventData.response_time} min` : '\u2014' },
    ], [eventData]);

    // Event object enriched with notes + reason_edits for dialog components
    const eventForDialog = useMemo(() => {
        if (!eventData) return null;
        return {
            ...eventData,
            event_id: eventId,
            notes: noteData,
            reason_edits: reasonEdits,
        };
    }, [eventData, eventId, noteData, reasonEdits]);

    // --------------------------------------------------------------------------
    // TAB LIST
    // --------------------------------------------------------------------------
    const tabs = useMemo(() => {
        const t = [{ key: 'details', label: 'Event Details' }];
        if (isCloud) t.push({ key: 'logs', label: 'Lifecycle Logs' });
        if (isGrandstream) t.push({ key: 'call-logs', label: 'Call Logs' });
        if (eventNotesActive) t.push({ key: 'notes', label: 'Notes' });
        return t;
    }, [isCloud, isGrandstream, eventNotesActive]);

    // --------------------------------------------------------------------------
    // RENDER
    // --------------------------------------------------------------------------
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
            <div className="dp-page">
                {/* Toast */}
                {toast && (
                    <div className={`dp-toast dp-toast--${toast.type}`}>
                        <span>{toast.message}</span>
                        <button className="dp-toast__close" onClick={() => setToast(null)}>{'\u2715'}</button>
                    </div>
                )}

                {/* Page Header */}
                <header className="dp-page-header">
                    <div className="dp-page-header__inner">
                        <div className="dp-breadcrumb-row">
                            <nav className="dp-breadcrumb">
                                <Link to="/" className="dp-breadcrumb__text">Home</Link>
                                <span className="dp-breadcrumb__separator">/</span>
                                <span className="dp-breadcrumb__current">Event Profile</span>
                            </nav>
                            <div className="dp-campus-row">
                                <label className="dp-campus-label">Campus:</label>
                                <span className="dp-campus-name">{campusName}</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Profile Banner */}
                <ProfileBanner
                    name={bannerName}
                    subtitle={`Event #${displayId}`}
                    metaFields={bannerMeta}
                    loading={loading}
                />

                {/* Tab Bar */}
                <div className="pp-tab-bar">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="pp-tab-content">
                    {activeTab === 'details' && (
                        <>
                            <BasicInfoSection
                                eventData={eventData}
                                isReasonEditable={isReasonEditable}
                                onEditReason={openReasonDialog}
                                tzOffset={tzOffset}
                            />
                            <EventFlowCard
                                eventData={eventData}
                                tzOffset={tzOffset}
                                isGrandstream={isGrandstream}
                                calls={callLogs}
                            />
                        </>
                    )}

                    {activeTab === 'logs' && isCloud && (
                        <LifecycleLogsTab
                            logs={lifecycleLogs}
                            loading={loading}
                            tzOffset={tzOffset}
                            eventId={eventId}
                        />
                    )}

                    {activeTab === 'call-logs' && isGrandstream && (
                        <CallLogsTab
                            callLogs={callLogs}
                            tzOffset={tzOffset}
                        />
                    )}

                    {activeTab === 'notes' && eventNotesActive && (
                        <NotesTab
                            notes={noteData}
                            editRights={editRights}
                            onAddNote={openAddNote}
                            onEditNote={openEditNote}
                            onDeleteNotes={handleDeleteNotes}
                            tzOffset={tzOffset}
                            eventId={eventId}
                        />
                    )}
                </div>

                {/* ---- Dialogs ---- */}

                {/* Note Dialog (Add / Edit) */}
                {noteDialogOpen && eventForDialog && (
                    <EventNoteDialog
                        event={eventForDialog}
                        prenoteData={prenoteData}
                        eventNotesPreform={eventNotesPreform}
                        eventNotesFreeform={eventNotesFreeform}
                        tzOffset={tzOffset}
                        onClose={() => { setNoteDialogOpen(false); setEditNoteData(null); }}
                        onSave={handleSaveNote}
                        saving={saving}
                        existingNote={editNoteData}
                    />
                )}

                {/* Edit Reason Dialog */}
                {reasonDialogOpen && eventForDialog && (
                    <EditReasonDialog
                        event={eventForDialog}
                        reasonCodes={reasonCodes}
                        onClose={() => setReasonDialogOpen(false)}
                        onSave={handleSaveReason}
                        saving={saving}
                    />
                )}

                {/* Delete Confirmation Dialog */}
                {deleteDialogOpen && (
                    <div className="pp-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
                        <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="pp-dialog__header">
                                <h2 className="pp-dialog__title">
                                    Delete Note{deleteNoteIds.length !== 1 ? 's' : ''}
                                </h2>
                                <button className="dp-drawer__close" onClick={() => setDeleteDialogOpen(false)}>
                                    {'\u2715'}
                                </button>
                            </div>
                            <div className="pp-dialog__body">
                                <p style={{ margin: 0 }}>
                                    Are you sure you want to delete {deleteNoteIds.length} note{deleteNoteIds.length !== 1 ? 's' : ''}?
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="pp-dialog__footer">
                                <button
                                    className="dp-btn dp-btn--cancel"
                                    onClick={() => setDeleteDialogOpen(false)}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="dp-btn dp-btn--danger"
                                    onClick={confirmDeleteNotes}
                                    disabled={saving}
                                >
                                    {saving ? 'Deleting\u2026' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </div>
        </>
    );
};

export default EventProfilePage;
